import Axios from "axios";
import { store } from "../redux/store";
import { notAuthorized } from "../redux/auth/actions";
import { DropDownAlert } from "../components";
import { AddNewNotif } from "../redux/notification/actions";

export const ApiServer = Axios.create({
    timeout: 60000,
});

ApiServer.interceptors.request.use(
    function (config) {
        const user = store.getState().Auth.user;
        config.headers.Authorization = user ? user.token : undefined;
        config.headers["X-DAYA-EMAIL"] = user ? user.email : undefined;
        config.headers["X-DAYA-ROLE"] = user ? user.user_type.type_name : undefined;
        // console.log("[ApiServer Request]: ", config);
        return config;
    },
    function (error) {
        // console.log('[ApiServer Request Error]: ', error.response);
        return Promise.reject(error);
    }
);

ApiServer.interceptors.response.use(
    function (response) {
        // console.log('[ApiServer Response]: ', response);
        return response;
    },
    function (error) {
        // console.log("[ApiServer Response Error]: ", error.response);
        const traceId = error.response.headers["x-daya-trace-id"];

        if (error.response && error.response.status === 401) {
            store.dispatch(notAuthorized());
        }
        if (error.response && error.response.status === 400 && error.response.data) {
            const { info } = error.response.data;
            if (info) {
                DropDownAlert.show("danger", `${info.err_message}, id: ${traceId}`);
                store.dispatch(AddNewNotif({ message: info.err_message, trace_id: traceId }));
            }
        }
        if (error.response && error.response.status === 409 && error.response.data) {
            const { info } = error.response.data;
            if (info) {
                DropDownAlert.show("danger", info.err_message);
                store.dispatch(AddNewNotif({ message: info.err_message, trace_id: traceId }));
            }
        }
        if (error.response && error.response.status === 500) {
            const { info } = error.response.data;
            DropDownAlert.show(
                "danger",
                `${info.err_message}, id: ${traceId}`
            );
            store.dispatch(AddNewNotif({ message: info.err_message, trace_id: traceId }));
        }
        if (!error.response) {
            const { info } = error.response.data;
            DropDownAlert.show("danger", `Mohon periksa koneksi jaringan anda!, id: ${traceId}`);
            store.dispatch(AddNewNotif({ message: info.err_message, trace_id: traceId }));
        }
        return Promise.reject(error);
    }
);

class ApiRequest {
    static post(url, reqBody) {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.post(`${process.env.REACT_APP_API_URL}${url}`, reqBody);
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static get(url, configs) {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.get(`${process.env.REACT_APP_API_URL}${url}`, configs);
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static put(url, configs) {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.put(`${process.env.REACT_APP_API_URL}${url}`, configs);
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static delete(url, configs) {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.delete(`${process.env.REACT_APP_API_URL}${url}`, configs);
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static async getDocument(params) {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.get(
                    `${process.env.REACT_APP_API_DOCUMENT}/api/public/v1/generate/${params}`,
                    {
                        responseType: "blob",
                    }
                );
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static async getDocumentPost(params, body) {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.post(
                    `${process.env.REACT_APP_API_DOCUMENT}/api/public/v1/generate/${params}`,
                    body,
                    {
                        responseType: "blob",
                    }
                );
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static async uploadBorrowersSFTP(id) {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.post(
                    `${process.env.REACT_APP_API_URL}/lender/sftp/upload/request/${id}`);
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static async getExportData(configs) {
        const { version = "v1", ...config } = configs;
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.get(`${process.env.REACT_APP_API_EXPORT}/api/private/${version}/`, {
                    ...config,
                    responseType: "blob",
                });
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static getRecording(url, configs) {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.get(`${process.env.REACT_APP_API_RECORDING}${url}`, configs);
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static uploadRecording(url, formData, configs){
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.post(`${process.env.REACT_APP_API_RECORDING}${url}`,formData, configs);
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * API helper to download SO List
     * with URL /api/private/v1/lpk
     * the respoonse is a raw excel file which lists down available SO
     * @param {Object} configs - the base config of the endpoint, the default value just gives out the versioning (v1)
     * @returns {Promise} - a promise result of the get method that returns the response if there's no error occured
     */
    static async downloadSOList({configs={}}) {
        const { version = "v1", ...config } = configs;
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.get(`${process.env.REACT_APP_API_EXPORT}/api/private/${version}/lpk`, {
                    ...config,
                    responseType: "blob",
                });
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static getCreditScoring(url, configs) {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.post(`${process.env.REACT_APP_API_CREDIT_SCORING}${url}`, configs);
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static getPefindoCreditScoring(url, configs){
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.get(`${process.env.REACT_APP_API_CREDIT_SCORING}${url}`, configs);
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }

    static async getExportApproval(configs) {
        const { version = "v1", ...config } = configs;
        return new Promise(async (resolve, reject) => {
            try {
                const response = await ApiServer.get(`${process.env.REACT_APP_API_EXPORT}/api/private/${version}/pre-approval`, {
                    ...config,
                    responseType: "blob",
                });
                resolve(response.data);
            } catch (error) {
                reject(error);
            }
        });
    }
}

export default ApiRequest;